/**
 * D360 portfolio service calls (direct browser calls to the platform host).
 *
 * These hit the platform base (VITE_API_BASE_URL) directly — NOT the
 * /streaming-economics API — using the shared `x-auth-token` session token
 * (see lib/api.ts getJson). Both calls are best-effort: a failure reads as an
 * empty list / no members, never a hard error that breaks the page.
 */
import { PLATFORM_BASE, getJson } from '@/lib/api';
import {
  parsePortfolioList,
  parsePortfolioMembers,
  type Portfolio,
  type PortfolioMembers,
} from '@/lib/portfolio';

/** Backend cap on `portfolios` ids per leaderboard request (400 above it). */
export const PORTFOLIO_IDS_MAX = 5000;

const PF_V3 = '/portfolios/v3/portfolios';
const PF_V1 = '/portfolios/v1/portfolios';

const platformUrl = (path: string) => `${PLATFORM_BASE}${path}`;

export const portfoliosApi = {
  /** List ALL the logged-in user's custom portfolios (every entity type). */
  list: (): Promise<Portfolio[]> => getJson<unknown>(platformUrl(PF_V3)).then(parsePortfolioList),

  /**
   * FULL membership (parrot_ids) from v1 `portfolio_items`. Only call this for
   * SELECTED portfolios — Parrot-shared generic lists hold 10k–40k ids.
   */
  members: (portfolioId: string): Promise<PortfolioMembers> =>
    getJson<unknown>(platformUrl(`${PF_V1}/${encodeURIComponent(portfolioId)}`)).then(
      parsePortfolioMembers,
    ),
};
